import React, { useState, useEffect, useCallback } from 'react';
import { getSocietyAdminData, API_BASE_URL } from '../services/api';
import './SocietyAdminInterestConfig.css';

interface InterestConfig {
  society_id: number;
  bill_type_key: string | null;
  interest_type: 'none' | 'fixed' | 'simple_percentage' | 'penalty_per_day';
  interest_rate: number;
  due_day_of_month: number;
  max_annual_rate: number;
  fixed_penalty: number;
  penalty_per_day_amount: number;
  is_active: number;
}

interface BillCategory {
  id: number;
  name: string;
  bill_type_key: string;
  is_active: number;
}

const DEFAULT_CONFIG: Omit<InterestConfig, 'society_id'> = {
  bill_type_key: null,
  interest_type: 'none',
  interest_rate: 0,
  due_day_of_month: 5,
  max_annual_rate: 21.0,
  fixed_penalty: 0,
  penalty_per_day_amount: 0,
  is_active: 0,
};

interface SocietyAdminInterestConfigProps {
  embedded?: boolean;
}

const SocietyAdminInterestConfig: React.FC<SocietyAdminInterestConfigProps> = ({ embedded = false }) => {
  const adminData = getSocietyAdminData();
  const societyId = adminData?.societyId;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(null);

  const [categories, setCategories] = useState<BillCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [allConfigs, setAllConfigs] = useState<InterestConfig[]>([]);

  const [interestType, setInterestType] = useState<string>('none');
  const [interestRate, setInterestRate] = useState('0');
  const [maxAnnualRate, setMaxAnnualRate] = useState('21');
  const [fixedPenalty, setFixedPenalty] = useState('0');
  const [penaltyPerDayAmount, setPenaltyPerDayAmount] = useState('0');

  const fetchCategories = useCallback(async () => {
    if (!societyId) return;
    try {
      const token = localStorage.getItem('admin_token');
      const resp = await fetch(`${API_BASE_URL}/bill-categories/${societyId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await resp.json();
      if (data.success && data.data) {
        setCategories(data.data.filter((c: BillCategory) => c.is_active));
      }
    } catch {
      // categories are optional for this page
    }
  }, [societyId]);

  const applyConfigToForm = (c: InterestConfig) => {
    setInterestType(c.interest_type);
    setFixedPenalty(String(c.fixed_penalty ?? 0));

    if (c.interest_type === 'fixed' || c.interest_type === 'simple_percentage') {
      setInterestRate(String(c.interest_rate));
    } else {
      setInterestRate('0');
    }

    if (c.interest_type === 'penalty_per_day') {
      setPenaltyPerDayAmount(String(c.penalty_per_day_amount ?? 0));
    } else {
      setPenaltyPerDayAmount('0');
    }

    if (c.interest_type === 'fixed' || c.interest_type === 'simple_percentage') {
      setMaxAnnualRate(String(c.max_annual_rate));
    } else {
      setMaxAnnualRate('21');
    }
  };

  const resetFormToDefaults = () => {
    setInterestType(DEFAULT_CONFIG.interest_type);
    setInterestRate(String(DEFAULT_CONFIG.interest_rate));
    setMaxAnnualRate(String(DEFAULT_CONFIG.max_annual_rate));
    setFixedPenalty(String(DEFAULT_CONFIG.fixed_penalty));
    setPenaltyPerDayAmount(String(DEFAULT_CONFIG.penalty_per_day_amount));
  };

  const fetchConfig = useCallback(async () => {
    if (!societyId) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const url = selectedCategory
        ? `${API_BASE_URL}/billing-interest-config/${societyId}?bill_type_key=${selectedCategory}`
        : `${API_BASE_URL}/billing-interest-config/${societyId}`;
      const resp = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await resp.json();
      if (data.success && data.data) {
        applyConfigToForm(data.data as InterestConfig);
      } else {
        resetFormToDefaults();
      }
    } catch (err: any) {
      setAlert({ type: 'error', message: err.message || 'Failed to load config' });
    } finally {
      setLoading(false);
    }
  }, [societyId, selectedCategory]);

  const fetchAllConfigs = useCallback(async () => {
    if (!societyId) return;
    try {
      const token = localStorage.getItem('admin_token');
      const resp = await fetch(`${API_BASE_URL}/billing-interest-config/${societyId}/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await resp.json();
      if (data.success && data.data) {
        setAllConfigs(data.data as InterestConfig[]);
      }
    } catch {
      // non-critical
    }
  }, [societyId]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);
  useEffect(() => { fetchConfig(); }, [fetchConfig]);
  useEffect(() => { fetchAllConfigs(); }, [fetchAllConfigs]);

  const handleSave = async () => {
    if (!societyId) return;
    setSaving(true);
    setAlert(null);
    try {
      const token = localStorage.getItem('admin_token');
      const body: Record<string, unknown> = {
        interest_type: interestType,
        interest_rate: Number(interestRate),
        max_annual_rate: Number(maxAnnualRate),
        fixed_penalty: Number(fixedPenalty),
        penalty_per_day_amount: interestType === 'penalty_per_day' ? Number(penaltyPerDayAmount) : 0,
        bill_type_key: selectedCategory || null,
      };
      const resp = await fetch(`${API_BASE_URL}/billing-interest-config/${societyId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      const data = await resp.json();
      if (data.success) {
        setAlert({ type: 'success', message: 'Interest configuration saved successfully' });
        fetchConfig();
        fetchAllConfigs();
      } else {
        setAlert({ type: 'error', message: data.error || 'Failed to save' });
      }
    } catch (err: any) {
      setAlert({ type: 'error', message: err.message || 'Failed to save config' });
    } finally {
      setSaving(false);
    }
  };

  const getInterestTypeLabel = (type: string): string => {
    switch (type) {
      case 'none': return 'None';
      case 'fixed': return 'Fixed (per month)';
      case 'simple_percentage': return 'Simple % (annual)';
      case 'penalty_per_day': return 'Penalty per day';
      default: return type;
    }
  };

  const getInterestSummary = (c: InterestConfig): string => {
    const parts: string[] = [];
    switch (c.interest_type) {
      case 'fixed': parts.push(`₹${c.interest_rate}/mo`); break;
      case 'simple_percentage': parts.push(`${c.interest_rate}% p.a. simple`); break;
      case 'penalty_per_day': parts.push(`₹${c.penalty_per_day_amount}/day`); break;
    }
    if (c.fixed_penalty > 0) parts.push(`+ ₹${c.fixed_penalty} one-time`);
    if (parts.length === 0 && c.fixed_penalty > 0) return `₹${c.fixed_penalty} one-time penalty`;
    return parts.join(' ') || '—';
  };

  const getCategoryName = (key: string | null): string => {
    if (!key) return 'All Categories (Global)';
    const cat = categories.find(c => c.bill_type_key === key);
    return cat ? cat.name : key;
  };

  if (loading) {
    return <div className="ic-loading">Loading interest configuration...</div>;
  }

  const scrollToEditor = () => {
    if (embedded) {
      document.getElementById('sad-interest-config-top')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div className={embedded ? 'ic-container ic-embedded' : 'ic-container'}>
      {!embedded && (
        <div className="ic-header">
          <h2>Interest Configuration</h2>
          <p className="ic-subtitle">
            Set overdue rules per bill category (Maintenance, Water, etc.) or as a society-wide default.
          </p>
        </div>
      )}

      <div
        id={embedded ? 'sad-interest-config-top' : undefined}
        className="ic-card ic-info-card"
      >
        <h3 className="ic-info-title">How categories work</h3>
        <ul className="ic-info-list">
          <li><strong>Global default</strong> — applies to every bill category unless you add a category-specific rule.</li>
          <li><strong>Per category</strong> — overrides the global rule for that type only (e.g. different rate for Water vs Maintenance).</li>
        </ul>
        <h3 className="ic-info-title" style={{ marginTop: 12 }}>Interest types</h3>
        <ul className="ic-info-list">
          <li><strong>Simple % (annual)</strong> — Principal × rate × (overdue days ÷ 365). Optional <em>fixed penalty</em> added once when overdue.</li>
          <li><strong>Penalty per day</strong> — Fixed ₹ each overdue day, <em>not</em> based on bill amount. Optional <em>fixed penalty</em> is a separate one-time charge if not paid by due date.</li>
          <li><strong>Fixed per month</strong> — Flat ₹ per month of delay (pro-rated by days).</li>
          <li><strong>None</strong> — No recurring interest; you can still set a one-time fixed penalty only.</li>
        </ul>
      </div>

      {alert && (
        <div className={`recon-alert recon-alert-${alert.type}`}>
          {alert.message}
          <button className="recon-alert-close" onClick={() => setAlert(null)}>&times;</button>
        </div>
      )}

      {categories.length > 0 && (
        <div className="ic-card" style={{ marginBottom: 16 }}>
          <div className="ic-form-group" style={{ marginBottom: 0 }}>
            <label className="ic-label">Apply To Category</label>
            <select
              className="ic-select"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <option value="">
                All Categories (Global Default)
                {allConfigs.some(c => !c.bill_type_key) ? ' ✓' : ''}
              </option>
              {categories.map((cat) => {
                const hasConfig = allConfigs.some(c => c.bill_type_key === cat.bill_type_key);
                return (
                  <option key={cat.bill_type_key} value={cat.bill_type_key}>
                    {cat.name}{hasConfig ? ' ✓' : ''}
                  </option>
                );
              })}
            </select>
            <span className="ic-help">
              {(() => {
                const currentKey = selectedCategory || null;
                const existingConfig = allConfigs.find(c => (c.bill_type_key || null) === currentKey);
                if (existingConfig) {
                  const label = selectedCategory
                    ? `"${categories.find(c => c.bill_type_key === selectedCategory)?.name}"`
                    : 'Global';
                  return `${label} already has a config (${getInterestTypeLabel(existingConfig.interest_type)}). Saving will update it.`;
                }
                return selectedCategory
                  ? `No config yet for "${categories.find(c => c.bill_type_key === selectedCategory)?.name}". Saving will create a new one.`
                  : 'Global config applies to all bill categories unless overridden per category.';
              })()}
            </span>
          </div>
        </div>
      )}

      <div className="ic-card">
        <div className="ic-form-group">
          <label className="ic-label">Interest Type</label>
          <select
            className="ic-select"
            value={interestType}
            onChange={(e) => {
              const newType = e.target.value;
              setInterestType(newType);
              if (newType !== 'fixed' && newType !== 'simple_percentage') setInterestRate('0');
              if (newType !== 'penalty_per_day') setPenaltyPerDayAmount('0');
              if (newType === 'none' || newType === 'penalty_per_day') setMaxAnnualRate('21');
            }}
          >
            <option value="none">None (no recurring interest)</option>
            <option value="simple_percentage">Simple percentage (annual rate)</option>
            <option value="penalty_per_day">Penalty per day (fixed ₹/day)</option>
            <option value="fixed">Fixed amount (per month)</option>
          </select>
          <span className="ic-help">
            {interestType === 'none' && 'No daily/monthly interest. You can still add a one-time fixed penalty below.'}
            {interestType === 'simple_percentage' && 'Formula: Principal × (Annual % ÷ 100) × (Overdue days ÷ 365). Capped by max annual rate.'}
            {interestType === 'penalty_per_day' && 'Charges the same ₹ every overdue day regardless of bill amount. Not a percentage of principal.'}
            {interestType === 'fixed' && 'Flat ₹ per month of delay (partial months pro-rated).'}
          </span>
        </div>

        {interestType === 'simple_percentage' && (
          <div className="ic-form-group">
            <label className="ic-label">Annual Interest Rate (%)</label>
            <input
              type="number"
              className="ic-input"
              value={interestRate}
              onChange={(e) => setInterestRate(e.target.value)}
              min="0"
              step="0.1"
            />
          </div>
        )}

        {interestType === 'fixed' && (
          <div className="ic-form-group">
            <label className="ic-label">Fixed Amount (₹ per month)</label>
            <input
              type="number"
              className="ic-input"
              value={interestRate}
              onChange={(e) => setInterestRate(e.target.value)}
              min="0"
              step="0.1"
            />
          </div>
        )}

        {interestType === 'penalty_per_day' && (
          <div className="ic-form-group">
            <label className="ic-label">Penalty Amount (₹ per day)</label>
            <input
              type="number"
              className="ic-input"
              value={penaltyPerDayAmount}
              onChange={(e) => setPenaltyPerDayAmount(e.target.value)}
              min="0"
              step="1"
            />
            <span className="ic-help">
              Example: ₹50/day × 10 overdue days = ₹500 interest (same whether the bill is ₹500 or ₹50,000).
            </span>
          </div>
        )}

        {(interestType === 'fixed' || interestType === 'simple_percentage') && (
          <div className="ic-form-group">
            <label className="ic-label">Maximum Annual Rate Cap (%)</label>
            <input
              type="number"
              className="ic-input"
              value={maxAnnualRate}
              onChange={(e) => setMaxAnnualRate(e.target.value)}
              min="0"
              max="50"
              step="0.5"
            />
            <span className="ic-help">Legal cap for simple/fixed interest (21% p.a. is common for housing societies).</span>
          </div>
        )}

        <div className="ic-form-group" style={{ borderTop: '1px solid #e5e7eb', paddingTop: 16, marginTop: 8 }}>
          <label className="ic-label">Fixed Penalty (₹ one-time)</label>
          <input
            type="number"
            className="ic-input"
            value={fixedPenalty}
            onChange={(e) => setFixedPenalty(e.target.value)}
            min="0"
            step="1"
          />
          <span className="ic-help">
            Charged once when the bill becomes overdue (not every day). Can be used alone with interest type
            &quot;None&quot;, or added on top of simple % or penalty-per-day.
          </span>
        </div>

        <div className="ic-actions">
          <button
            className="recon-btn recon-btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Configuration'}
          </button>
        </div>
      </div>

      {allConfigs.length > 0 && (
        <div className="ic-card" style={{ marginTop: 24 }}>
          <h3 style={{ margin: '0 0 4px 0', fontSize: 16, fontWeight: 600, color: '#1f2937' }}>
            Configured Interest Rules
          </h3>
          <p style={{ margin: '0 0 16px 0', fontSize: 13, color: '#6b7280' }}>
            Overview of all interest/penalty rules configured for this society.
          </p>
          <table className="ic-config-table">
            <thead>
              <tr>
                <th>Category</th>
                <th>Interest Type</th>
                <th>Rate / Amount</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {allConfigs.map((c, idx) => (
                <tr key={idx} className={c.is_active ? '' : 'ic-row-inactive'}>
                  <td style={{ fontWeight: 500 }}>{getCategoryName(c.bill_type_key)}</td>
                  <td>
                    <span className={`ic-type-badge ic-type-${c.interest_type}`}>
                      {getInterestTypeLabel(c.interest_type)}
                    </span>
                  </td>
                  <td>{getInterestSummary(c)}</td>
                  <td>
                    <span className={c.is_active ? 'ic-status-active' : 'ic-status-inactive'}>
                      {c.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <button
                      className="recon-btn recon-btn-outline recon-btn-sm"
                      onClick={() => {
                        setSelectedCategory(c.bill_type_key || '');
                        scrollToEditor();
                      }}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SocietyAdminInterestConfig;
